import { useEffect, useState } from "react";

function secondsUntil(deadline: number): number {
  return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
}

interface ResendCountdownProps {
  /** Absolute deadline (ms epoch) the countdown ticks down to. */
  deadline: number;
  /** Renders the label for the given whole seconds remaining. */
  children: (secondsRemaining: number) => React.ReactNode;
}

/**
 * Display-only leaf that ticks the seconds remaining until `deadline` once per
 * second. Keeping this state here (rather than in the parent login form) means
 * only this node re-renders each second — the rest of the form, including the
 * OTP input, stays put. Recomputed from the wall clock so it stays accurate
 * across a backgrounded/throttled webview.
 */
export function ResendCountdown({ deadline, children }: ResendCountdownProps): React.ReactElement {
  const [secondsRemaining, setSecondsRemaining] = useState(() => secondsUntil(deadline));

  useEffect(() => {
    function tick(): void {
      setSecondsRemaining(secondsUntil(deadline));
    }
    tick();
    const intervalId = window.setInterval(tick, 1000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [deadline]);

  return <>{children(secondsRemaining)}</>;
}
